import React, { useMemo } from 'react';

import type { Capabilities } from '@rtkwlf/rendall';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import { BreadcrumbsRouter } from '@packages/fenrir/components';
import { useFeatureFlag } from '@packages/platform/feature-flags';

import AgentGroupPageBreadcrumbs from '../agent-configurations/agent-grouping/agent-group-page-breadcrumbs';

export type AgentSettingsPageInfoType = {
  key: string;
  internalOnly: boolean;
  linkPattern: string;
  link: string;
  legacyCapabilityRequirements: Partial<Capabilities>;
  showNavbar: boolean;
  breadcrumbs: () => React.ReactNode;
};

const TabValues = {
  ExternalConfigurations: 'configuration',
  InternalConfiguration: 'internalConfiguration',
  AgentAdministration: 'agentAdministration',
  GroupCreate: 'groupCreate',
  GroupEdit: 'groupEdit',
};

declare module '@platform/core/feature-flags' {
  interface FeatureFlags {
    SHOW_AGENT_GROUPING?: boolean;
  }
}

export const useAgentSettingsPages = () => {
  const { t } = useTranslation('telemetryAgents');
  const [shouldShowAgentGrouping] = useFeatureFlag('SHOW_AGENT_GROUPING');
  const [isIaModeEnabled] = useFeatureFlag('UNIFIED_PORTAL_IA');

  return useMemo(() => {
    const pages: AgentSettingsPageInfoType[] = [
      {
        key: TabValues.ExternalConfigurations,
        internalOnly: false,
        linkPattern: isIaModeEnabled
          ? '/:organizationId/telemetry/agents/settings/configuration'
          : '/telemetry/agents/settings/configuration',
        link: generatePath('/telemetry/agents/settings/configuration'),
        legacyCapabilityRequirements: { canViewAgentSettings: true },
        showNavbar: true,
        breadcrumbs: () => (
          <BreadcrumbsRouter
            crumbs={[
              {
                label: t('agentSettings.breadcrumbs.agents'),
                to: generatePath('/telemetry/agents'),
              },
              {
                label: t('agentSettings.breadcrumbs.agentSettings'),
                to: generatePath('/telemetry/agents/settings'),
              },
            ]}
          />
        ),
      },
      {
        key: TabValues.InternalConfiguration,
        internalOnly: true,
        linkPattern: isIaModeEnabled
          ? '/:organizationId/telemetry/agents/settings/internal-configuration'
          : '/telemetry/agents/settings/internal-configuration',
        link: generatePath('/telemetry/agents/settings/internal-configuration'),
        legacyCapabilityRequirements: { canViewAgentSettings: true },
        showNavbar: true,
        breadcrumbs: () => (
          <BreadcrumbsRouter
            crumbs={[
              {
                label: t('agentSettings.breadcrumbs.agents'),
                to: generatePath('/telemetry/agents'),
              },
              {
                label: t('agentSettings.breadcrumbs.agentSettings'),
                to: generatePath('/telemetry/agents/settings'),
              },
            ]}
          />
        ),
      },
    ];
    if (shouldShowAgentGrouping) {
      pages.push({
        key: TabValues.GroupCreate,
        internalOnly: false,
        linkPattern: isIaModeEnabled
          ? '/:organizationId/telemetry/agents/settings/create-group'
          : '/telemetry/agents/settings/create-group',
        link: generatePath('/telemetry/agents/settings/create-group'),
        legacyCapabilityRequirements: { canViewAgentSettings: true },
        showNavbar: false,
        breadcrumbs: () => (
          <BreadcrumbsRouter
            crumbs={[
              {
                label: t('agentSettings.breadcrumbs.agents'),
                to: generatePath('/telemetry/agents'),
              },
              {
                label: t('agentSettings.breadcrumbs.agentSettings'),
                to: generatePath('/telemetry/agents/settings/configuration'),
              },
              {
                label: t('agentSettings.breadcrumbs.newGroup'),
                to: generatePath('/telemetry/agents/settings/create-group'),
              },
            ]}
          />
        ),
      });
      pages.push({
        key: TabValues.GroupEdit,
        internalOnly: false,
        linkPattern: isIaModeEnabled
          ? '/:organizationId/telemetry/agents/settings/group/:groupId'
          : '/telemetry/agents/settings/group/:groupId',
        link: generatePath('/telemetry/agents/configurations'),
        legacyCapabilityRequirements: { canViewAgentSettings: true },
        showNavbar: false,
        breadcrumbs: () => <AgentGroupPageBreadcrumbs />,
      });
    }
    return pages;
  }, [t, shouldShowAgentGrouping, isIaModeEnabled]);
};
